import { motion } from 'framer-motion';
import {
  Briefcase,
  Calendar,
  Check,
  ChevronDown,
  ChevronRight,
  Filter,
  Mail,
  MessageSquare,
  Search,
  Star
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: 'beforeChildren',
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 300, damping: 24 },
  },
};

const ApplicationsList = () => {
  const [searchParams] = useSearchParams();
  const jobIdFromUrl = searchParams.get('job');
  
  // States
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: [],
    job: jobIdFromUrl ? [jobIdFromUrl] : [],
    dateRange: 'all',
  });
  const [sortBy, setSortBy] = useState('recent');
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Data fetched from API
  const [jobs, setJobs] = useState([]);
  const [baseApplications, setBaseApplications] = useState([]);
  
  // Fetch employer jobs and applications on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // 1) Fetch employer jobs
        const jobsRes = await api.get('/jobs/employer/my-jobs', {
          params: { limit: 100 }
        });
        const fetchedJobs = (jobsRes.data?.data?.jobs || jobsRes.data?.data || []).map(j => ({
          id: j._id,
          title: j.title
        }));
        setJobs(fetchedJobs);

        // 2) Fetch applications to these jobs
        // We could call once without filters to get all
        const appsRes = await api.get('/applications', {
          params: { page: 1, limit: 100 }
        });

        const fetchedAppsRaw = appsRes.data?.data?.applications || appsRes.data?.data || [];

        const mappedApps = fetchedAppsRaw.map(app => {
          const candidate = app.user || app.applicant || {};
          const job = app.job || {};
          return {
            id: app._id,
            candidate: {
              id: candidate._id,
              name: `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim(),
              email: candidate.email,
              avatar: candidate.firstName ? candidate.firstName.charAt(0).toUpperCase() : 'U'
            },
            job: {
              id: job._id,
              title: job.title
            },
            status: app.status,
            appliedDate: app.appliedAt || app.createdAt,
            matchScore: app.matchScore || 0,
            resume: candidate.resume,
            coverLetter: !!app.coverLetter,
            lastActivity: app.updatedAt || app.createdAt
          };
        });

        setBaseApplications(mappedApps);
        setApplications(mappedApps);
      } catch (err) {
        console.error('Failed to fetch applications', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);
  
  // Filter applications based on search and filters
  useEffect(() => {
    if (loading) return;
    
    let filtered = [...baseApplications];
    
    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(app => 
        (app.candidate.name || '').toLowerCase().includes(query) ||
        (app.candidate.email || '').toLowerCase().includes(query) ||
        (app.job.title || '').toLowerCase().includes(query)
      );
    }
    
    // Apply status filter
    if (filters.status.length > 0) {
      filtered = filtered.filter(app => filters.status.includes(app.status));
    }
    
    // Apply job filter
    if (filters.job.length > 0) {
      filtered = filtered.filter(app => filters.job.includes(app.job.id));
    }
    
    // Apply date range filter
    if (filters.dateRange !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filtered = filtered.filter(app => {
        const appliedDate = new Date(app.appliedDate);
        const diffTime = Math.abs(today - appliedDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (filters.dateRange === 'today') {
          return diffDays === 0;
        } else if (filters.dateRange === 'week') {
          return diffDays <= 7;
        } else if (filters.dateRange === 'month') {
          return diffDays <= 30;
        }
        
        return true;
      });
    }
    
    // Apply sorting
    switch (sortBy) {
      case 'name':
        filtered.sort((a, b) => a.candidate.name.localeCompare(b.candidate.name));
        break;
      case 'match':
        filtered.sort((a, b) => (b.matchScore || 0) - (a.matchScore || 0));
        break;
      case 'recent':
      default:
        filtered.sort((a, b) => new Date(b.appliedDate) - new Date(a.appliedDate));
        break;
    }
    
    setApplications(filtered);
  }, [searchQuery, filters, sortBy, loading, baseApplications]);
  
  // Handle filter changes
  const handleFilterChange = (type, value) => {
    setFilters(prev => {
      const updated = { ...prev };
      
      if (type === 'status' || type === 'job') {
        if (updated[type].includes(value)) {
          updated[type] = updated[type].filter(item => item !== value);
        } else {
          updated[type] = [...updated[type], value];
        }
      } else {
        updated[type] = value;
      }
      
      return updated;
    });
  };
  
  // Clear filters
  const clearFilters = () => {
    setFilters({
      status: [],
      job: [],
      dateRange: 'all',
    });
    setSearchQuery('');
  };
  
  // Get count of active filters
  const getActiveFilterCount = () => {
    let count = 0;
    count += filters.status.length;
    count += filters.job.length;
    if (filters.dateRange !== 'all') count += 1;
    return count;
  };
  
  // Get status badge style
  const getStatusStyle = (status) => {
    switch (status) {
      case 'New':
        return 'bg-primary-100 text-primary-700';
      case 'Reviewed':
        return 'bg-accent-100 text-accent-700';
      case 'Interview':
        return 'bg-warning-100 text-warning-700';
      case 'Offer':
        return 'bg-success-100 text-success-700';
      case 'Rejected':
        return 'bg-secondary-100 text-secondary-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };
  
  return (
    <motion.div
      className="space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Page header */}
      <motion.div 
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        variants={itemVariants}
      >
        <div>
          <h1 className="page-title">Applications</h1>
          <p className="text-gray-600 mt-1">
            {applications.length} applications across {jobs.length} open positions
          </p>
        </div>
      </motion.div>

      {/* Search and filters */}
      <motion.div variants={itemVariants}>
        <div className="bg-white rounded-xl shadow-soft p-4 border border-gray-100">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            {/* Search input */}
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={18} className="text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by candidate name, email or job title"
                className="block w-full pl-10 pr-4 py-2.5 text-gray-900 bg-gray-50 border border-gray-200 rounded-lg font-body text-sm focus:outline-none focus:ring-1 focus:ring-primary-600 focus:border-primary-600"
              />
            </div>

            {/* Filter button */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="btn-outline flex items-center"
              >
                <Filter size={16} className="mr-2" />
                Filters
                {getActiveFilterCount() > 0 && (
                  <span className="ml-2 bg-primary-100 text-primary-700 text-xs rounded-full px-2 py-0.5">
                    {getActiveFilterCount()}
                  </span>
                )}
              </button>

              {/* Sort dropdown */}
              <div className="relative">
                <div className="flex items-center space-x-2">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="select-field py-2.5 pl-4 pr-10 appearance-none bg-white"
                  >
                    <option value="recent">Sort by: Recent</option>
                    <option value="name">Sort by: Name</option>
                    <option value="match">Sort by: Match Score</option>
                  </select>
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <ChevronDown size={16} className="text-gray-500" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filters section */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="font-accent font-medium text-sm text-gray-700">Filter by:</span>
                {getActiveFilterCount() > 0 && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
                  >
                    Clear all filters
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Status filters */}
                <div>
                  <p className="block mb-2 text-sm font-semibold text-gray-700 font-accent">
                    Status
                  </p>
                  <div className="space-y-2">
                    {['New', 'Reviewed', 'Interview', 'Offer', 'Rejected'].map((status) => (
                      <label
                        key={status}
                        className="flex items-center space-x-2 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={filters.status.includes(status)}
                          onChange={() => handleFilterChange('status', status)}
                          className="hidden"
                        />
                        <div
                          className={`w-4 h-4 rounded flex items-center justify-center border ${
                            filters.status.includes(status)
                              ? 'bg-primary-600 border-primary-600'
                              : 'border-gray-300'
                          }`}
                        >
                          {filters.status.includes(status) && (
                            <Check size={12} className="text-white" />
                          )}
                        </div>
                        <span className="text-sm text-gray-700">{status}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Job filters */}
                <div>
                  <p className="block mb-2 text-sm font-semibold text-gray-700 font-accent">
                    Job Position
                  </p>
                  <div className="space-y-2">
                    {jobs.map((job) => (
                      <label
                        key={job.id}
                        className="flex items-center space-x-2 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={filters.job.includes(job.id)}
                          onChange={() => handleFilterChange('job', job.id)}
                          className="hidden"
                        />
                        <div
                          className={`w-4 h-4 rounded flex items-center justify-center border ${
                            filters.job.includes(job.id)
                              ? 'bg-primary-600 border-primary-600'
                              : 'border-gray-300'
                          }`}
                        >
                          {filters.job.includes(job.id) && (
                            <Check size={12} className="text-white" />
                          )}
                        </div>
                        <span className="text-sm text-gray-700">{job.title}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Date range filters */}
                <div>
                  <p className="block mb-2 text-sm font-semibold text-gray-700 font-accent">
                    Date Range
                  </p>
                  <div className="space-y-2">
                    {[
                      { value: 'all', label: 'All Time' },
                      { value: 'today', label: 'Today' },
                      { value: 'week', label: 'Last 7 Days' },
                      { value: 'month', label: 'Last 30 Days' },
                    ].map((option) => (
                      <label
                        key={option.value}
                        className="flex items-center space-x-2 cursor-pointer"
                      >
                        <input
                          type="radio"
                          checked={filters.dateRange === option.value}
                          onChange={() => handleFilterChange('dateRange', option.value)}
                          className="hidden"
                        />
                        <div
                          className={`w-4 h-4 rounded-full flex items-center justify-center border ${
                            filters.dateRange === option.value
                              ? 'border-primary-600'
                              : 'border-gray-300'
                          }`}
                        >
                          {filters.dateRange === option.value && (
                            <div className="w-2 h-2 rounded-full bg-primary-600"></div>
                          )}
                        </div>
                        <span className="text-sm text-gray-700">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Applications list */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="loading-spinner w-8 h-8 border-4"></div>
        </div>
      ) : applications.length === 0 ? (
        <motion.div 
          className="text-center py-12"
          variants={itemVariants}
        >
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <Briefcase size={24} className="text-gray-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 font-heading">No applications found</h2>
          <p className="mt-1 text-gray-500">
            Try adjusting your search or filters to find what you're looking for.
          </p>
          <button
            onClick={clearFilters}
            className="mt-4 btn-secondary"
          >
            Clear Filters
          </button>
        </motion.div>
      ) : (
        <motion.div variants={itemVariants}>
          <div className="bg-white rounded-xl shadow-soft border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 font-accent uppercase tracking-wider">
                      Candidate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 font-accent uppercase tracking-wider">
                      Job
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 font-accent uppercase tracking-wider">
                      Applied Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 font-accent uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 font-accent uppercase tracking-wider">
                      Match
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 font-accent uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {applications.map((application) => (
                    <tr key={application.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-primary-700 font-medium">
                              {application.candidate.avatar}
                            </span>
                          </div>
                          <div className="ml-3">
                            <Link 
                              to={`/candidates/${application.candidate.id}`}
                              state={{ fromApplications: true, applicationId: application.id }}
                              className="text-sm font-medium text-gray-900 hover:text-primary-600"
                            >
                              {application.candidate.name}
                            </Link>
                            <div className="flex items-center text-xs text-gray-500">
                              <Mail size={12} className="mr-1" />
                              {application.candidate.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Briefcase size={16} className="text-gray-500 mr-2" />
                          <Link 
                            to={`/jobs/${application.job.id}`}
                            className="text-sm text-gray-900 hover:text-primary-600"
                          >
                            {application.job.title}
                          </Link>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-700">
                          <Calendar size={16} className="text-gray-500 mr-2" />
                          {formatDate(application.appliedDate)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusStyle(
                            application.status
                          )}`}
                        >
                          {application.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Star
                            size={16}
                            className="text-warning-500 mr-1"
                            fill="currentColor"
                          />
                          <span className="text-sm font-medium">{application.matchScore}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700">
                            <MessageSquare size={16} />
                          </button>
                          <Link
                            to={`/candidates/${application.candidate.id}`}
                            state={{ fromApplications: true, applicationId: application.id }}
                            className="p-1.5 rounded-lg hover:bg-gray-100 text-primary-600 hover:text-primary-700"
                          >
                            <ChevronRight size={16} />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default ApplicationsList; 